import React, { useEffect, useState } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './context/AuthContext';
import Login from './pages/Login';
import Signup from './pages/Signup';
import Dashboard from './pages/Dashboard';
import Clients from './pages/Clients';
import Messages from './pages/Messages';
import NewSession from './pages/NewSession';
import Settings from './pages/Settings';
import ResetPassword from './pages/ResetPassword';
import SupportWidget from './components/SupportWidget';
import ClientProfile from './pages/ClientProfile';
import Layout from './components/Layout';
import ClientPortal from './pages/ClientPortal';
import ClientPortalLogin from './pages/ClientPortalLogin';
import MagicLinkRedeem from './pages/MagicLinkRedeem';
import ClientLogin from './pages/ClientLogin';
import PublicMarketingPage from './pages/PublicMarketingPage';
import Groups from './pages/Groups';
import GroupDetail from './pages/GroupDetail';
import Schedule from './pages/Schedule';
import Sessions from './pages/Sessions';
import PendingRecordings from './pages/PendingRecordings';
import ImportRecording from './pages/ImportRecording';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import Marketing from './pages/Marketing';
import Admin from './pages/Admin';
import OnboardingWizard from './components/OnboardingWizard';
import AppTour from './components/AppTour';
import NewSessionModal from './components/NewSessionModal';
import { savePushSubscription } from './api/client';
import LoadingLeaf from './components/LoadingLeaf';

const PrivateRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <LoadingLeaf fullScreen size={72} />;
  return user ? <Layout>{children}</Layout> : <Navigate to="/login" />;
};

export default function App() {
  const { user, profile, profileLoading, profileError } = useAuth();
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [showTour, setShowTour] = useState(false);
  const [onboardingChecked, setOnboardingChecked] = useState(false);

  useEffect(() => {
    if (!user || profileLoading) return;
    // Wait until the profile has actually resolved — either loaded or errored.
    // A transient profile===null (before the fetch resolves) must not be read
    // as "onboarding not completed", or the wizard flashes on every reload.
    if (!profile && !profileError) return;
    setShowOnboarding(!profileError && !profile?.onboarding_completed);
    setOnboardingChecked(true);
  }, [user, profile, profileLoading, profileError]);

  useEffect(() => {
    if (!user) return;

    // Push notifications setup
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) return;
    navigator.serviceWorker.register('/sw.js').then(async (reg) => {
      try {
        const permission = await Notification.requestPermission();
        if (permission !== 'granted') return;
        const sub = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: process.env.REACT_APP_VAPID_PUBLIC_KEY
        });
        await savePushSubscription(sub.toJSON());
      } catch (err) {
        console.log('Push setup error:', err);
      }
    }).catch(err => console.log('SW registration failed:', err));
  }, [user]);

  return (
    <>
      <Routes>
        <Route path="/login" element={user ? <Navigate to="/" /> : <Login />} />
        <Route path="/signup" element={user ? <Navigate to="/" /> : <Signup />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/" element={<PrivateRoute><Dashboard /></PrivateRoute>} />
        <Route path="/clients" element={<PrivateRoute><Clients /></PrivateRoute>} />
        <Route path="/messages" element={<PrivateRoute><Messages /></PrivateRoute>} />
        <Route path="/session/new" element={<PrivateRoute><NewSession /></PrivateRoute>} />
        <Route path="/settings" element={<PrivateRoute><Settings /></PrivateRoute>} />
        <Route path="/clients/:id" element={<PrivateRoute><ClientProfile /></PrivateRoute>} />
        <Route path="/portal/:accessCode" element={<ClientPortalLogin />} />
        <Route path="/portal/:accessCode/home" element={<ClientPortal />} />
        <Route path="/portal/:accessCode/magic/:magicToken" element={<MagicLinkRedeem />} />
        <Route path="/site/:slug" element={<PublicMarketingPage />} />
        <Route path="/client-login" element={<ClientLogin />} />
        <Route path="/sessions" element={<PrivateRoute><Sessions /></PrivateRoute>} />
        <Route path="/groups" element={<PrivateRoute><Groups /></PrivateRoute>} />
        <Route path="/groups/:id" element={<PrivateRoute><GroupDetail /></PrivateRoute>} />
        <Route path="/schedule" element={<PrivateRoute><Schedule /></PrivateRoute>} />
        <Route path="/pending-recordings" element={<PrivateRoute><PendingRecordings /></PrivateRoute>} />
        <Route path="/import-recording/:source/:externalId" element={<PrivateRoute><ImportRecording /></PrivateRoute>} />
        <Route path="/marketing" element={<PrivateRoute><Marketing /></PrivateRoute>} />
        <Route path="/admin" element={<PrivateRoute><Admin /></PrivateRoute>} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/terms" element={<TermsOfService />} />
      </Routes>
      {user && <SupportWidget onReplayTour={() => setShowTour(true)} />}
      {user && onboardingChecked && showOnboarding && (
        <OnboardingWizard
          userEmail={user.email}
          onComplete={() => setShowOnboarding(false)}
          onStartTour={() => setShowTour(true)}
        />
      )}
      {showTour && <AppTour onFinish={() => setShowTour(false)} />}
      {user && <NewSessionModal />}
    </>
  );
}
